import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import RichTextEditor from '../components/RichTextEditor';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { 
  CheckCircle2, 
  FileText, 
  Save,
  Loader2,
  Layers,
  AlertCircle,
  Clock
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://kroytechnologygroup.com/wp-content/uploads/2022/01/KTG-Final-Logo-Flat-300x200.png";

export default function DepartmentPortalPage() {
  const { deptToken } = useParams();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  const [lastAutoSaved, setLastAutoSaved] = useState(null);
  const [autoSaving, setAutoSaving] = useState(false);
  
  const [submissionData, setSubmissionData] = useState(null);
  const [formData, setFormData] = useState({});
  const saveTimeoutRef = useRef(null);

  useEffect(() => {
    loadDeptData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deptToken]);

  const loadDeptData = async () => {
    try {
      const response = await axios.get(`${API_URL}/department/${deptToken}`);
      setSubmissionData(response.data);
      setFormData(response.data.data || {});
      if (response.data.department_completed) {
        setSubmitted(true);
      }
    } catch (err) {
      setError('Invalid or expired department access link');
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = (fieldName, value) => {
    setFormData(prev => ({ ...prev, [fieldName]: value }));
  };

  // Auto-save logic
  const autoSave = useCallback(async (data) => {
    setAutoSaving(true);
    try {
      await axios.put(`${API_URL}/department/${deptToken}`, { data });
      setLastAutoSaved(new Date());
    } catch { /* silent */ }
    finally { setAutoSaving(false); }
  }, [deptToken]);

  useEffect(() => {
    if (!submissionData || loading || submitted) return;
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(() => autoSave(formData), 3000);
    return () => { if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current); };
  }, [formData, submissionData, loading, submitted, autoSave]);

  const handleSubmit = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/department/${deptToken}`, { data: { ...formData, __submit__: true } });
      setSubmitted(true);
      toast.success('Your responses have been submitted!');
    } catch (err) {
      toast.error('Failed to submit responses');
    } finally {
      setSaving(false);
    }
  };

  const renderField = (field) => {
    const value = formData[field.name] || '';
    switch (field.type) {
      case 'textarea':
        return <RichTextEditor value={value} onChange={html => handleFieldChange(field.name, html)} placeholder={field.placeholder || `Enter ${field.label.toLowerCase()}`} data-testid={`dept-field-${field.name}`} />;
      case 'select':
        return (
          <Select value={value} onValueChange={val => handleFieldChange(field.name, val)}>
            <SelectTrigger data-testid={`dept-field-${field.name}`}><SelectValue placeholder={`Select ${field.label.toLowerCase()}`} /></SelectTrigger>
            <SelectContent>{field.options?.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
          </Select>
        );
      case 'number':
        return <Input id={field.name} type="number" value={value} onChange={e => handleFieldChange(field.name, e.target.value)} placeholder={field.placeholder || `Enter ${field.label.toLowerCase()}`} data-testid={`dept-field-${field.name}`} />;
      case 'email':
        return <Input id={field.name} type="email" value={value} onChange={e => handleFieldChange(field.name, e.target.value)} placeholder={field.placeholder || 'email@example.com'} data-testid={`dept-field-${field.name}`} />;
      case 'date':
        return <Input id={field.name} type="date" value={value} onChange={e => handleFieldChange(field.name, e.target.value)} data-testid={`dept-field-${field.name}`} />;
      default:
        return <Input id={field.name} type="text" value={value} onChange={e => handleFieldChange(field.name, e.target.value)} placeholder={field.placeholder || `Enter ${field.label.toLowerCase()}`} data-testid={`dept-field-${field.name}`} />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#FF6B35] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-500">Loading...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-8">
        <div className="w-full max-w-md text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <AlertCircle className="h-8 w-8 text-red-600" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>Access Denied</h1>
          <p className="text-slate-600">{error}</p>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-8">
        <div className="w-full max-w-md text-center">
          <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="h-8 w-8 text-emerald-600" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>Thank You!</h1>
          <p className="text-slate-600 mb-4">Your responses have been submitted successfully.</p>
          <p className="text-slate-500 text-sm">
            <strong>{submissionData?.company_name}</strong> has been notified that the <strong>{submissionData?.department_name}</strong> department has completed its sections.
          </p>
        </div>
      </div>
    );
  }

  const deptFields = submissionData?.department_fields || [];
  const templateSections = submissionData?.template_sections || [];
  const relevantSections = templateSections.filter(section =>
    section.fields.some(field => deptFields.includes(field.name))
  );

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="AuditGuard" className="h-10 w-auto" />
            <div>
              <span className="font-semibold text-slate-900 block" style={{ fontFamily: 'Outfit, sans-serif' }}>AuditGuard</span>
              <span className="text-xs text-slate-500">Department Portal</span>
            </div>
          </div>
          {lastAutoSaved && (
            <div className="flex items-center gap-1.5 text-xs text-slate-400" data-testid="dept-autosave-indicator">
              {autoSaving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Clock className="h-3 w-3" />}
              {autoSaving ? 'Saving...' : `Auto-saved at ${lastAutoSaved.toLocaleTimeString()}`}
            </div>
          )}
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-teal-50 border border-teal-200 rounded-xl p-6 mb-8" data-testid="dept-portal-banner">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <Layers className="h-6 w-6 text-teal-600" />
            </div>
            <div>
              <h1 className="text-xl font-semibold text-slate-900 mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {submissionData?.template_name}
              </h1>
              <p className="text-slate-600">
                <strong>{submissionData?.company_name}</strong> has assigned the following fields to the <strong>{submissionData?.department_name}</strong> department.
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-8">
          {relevantSections.map(section => {
            const sectionDeptFields = section.fields.filter(f => deptFields.includes(f.name));
            if (sectionDeptFields.length === 0) return null;
            return (
              <div key={section.id} className="bg-white rounded-xl border border-slate-200 p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-[#FF6B35]/10 rounded-lg flex items-center justify-center">
                    <FileText className="h-5 w-5 text-[#FF6B35]" />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>{section.title}</h2>
                    <p className="text-sm text-slate-500">{section.description}</p>
                  </div>
                </div>
                <div className="space-y-6">
                  {sectionDeptFields.map(field => (
                    <div key={field.name} className="space-y-2">
                      <Label htmlFor={field.name} className="flex items-center gap-2">
                        {field.label}
                        {field.required && <span className="text-red-500">*</span>}
                      </Label>
                      {renderField(field)}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-8 flex justify-end">
          <Button onClick={handleSubmit} className="btn-primary px-8 py-3" disabled={saving} data-testid="dept-submit-btn">
            {saving ? (
              <div className="flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" />Submitting...</div>
            ) : (
              <div className="flex items-center gap-2"><Save className="h-4 w-4" />Submit Responses</div>
            )}
          </Button>
        </div>
      </main>
    </div>
  );
}
